import React from 'react';
import { useSearchParams } from 'react-router-dom';
import ClassificationFilter from './filters/ClassificationFilter';
import StatusFilter from './filters/StatusFilter';
import TagsFilter from './filters/TagsFilter';
import CategoryFilter from './filters/CategoryFilter';
import ActiveFilterItemIndicator from './filters/ActiveFilterItemIndicator';
import ActiveFiltersContainer from './filters/ActiveFiltersContainer';
import ClassificationScoreFilter from './filters/ClassificationScoreFilter';
import { usePlatformData } from '../hooks/usePlatformData';
import { IoGridOutline } from 'react-icons/io5';
import { FaList } from 'react-icons/fa';
import { Button } from '../catalyst/button';
import ProjectIdInputFilter from './filters/ProjectIdFilter';
import { useFilterState } from './filters/useFilterState';

const STORAGE_KEY = 'document-pages-filters';
const VIEW_MODE_KEY = 'document-pages-view-mode';

const categoryFilterLabel = (value: string) =>
  value === '(null)' ? 'Uncategorized' : value === '(not-null)' ? 'Has category' : value;

interface DocumentPagesFiltersProps {
  onFilterChange: (filters: DocumentPagesFilters) => void;
  onViewModeChange: (viewMode: 'list' | 'grid') => void;
}

export interface DocumentPagesFilters {
  projectId: string;
  category: string | null;
  status: 'done' | 'pending' | 'error' | 'invalid' | '';
  classification: string | null;
  classificationScore: string;
  tags: string[];
}

const defaultFilters: DocumentPagesFilters = {
  projectId: '',
  category: null,
  status: '',
  classification: null,
  classificationScore: '',
  tags: [],
};

const STATUS_VALUES: DocumentPagesFilters['status'][] = ['done', 'pending', 'error', 'invalid'];

// Seed filters from URL params (e.g. a deep link from Project View to failed
// pages). Only recognised keys are applied, and `status` is validated against
// the filter union so an arbitrary query value can't be injected.
function urlOverrides(searchParams: URLSearchParams): Partial<DocumentPagesFilters> | undefined {
  const overrides: Partial<DocumentPagesFilters> = {};
  const projectId = searchParams.get('projectId');
  if (projectId) {
    overrides.projectId = projectId;
  }
  const status = searchParams.get('status');
  if (status && STATUS_VALUES.includes(status as DocumentPagesFilters['status'])) {
    overrides.status = status as DocumentPagesFilters['status'];
  }
  return Object.keys(overrides).length > 0 ? overrides : undefined;
}

export default function DocumentFilters({ onFilterChange, onViewModeChange }: DocumentPagesFiltersProps) {
  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>(() => {
    const saved = sessionStorage.getItem(VIEW_MODE_KEY);
    return saved ? (saved as 'list' | 'grid') : 'list';
  });
  const { categories } = usePlatformData();
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadId = searchParams.get('uploadId');
  const onlyIds = searchParams.getAll('ids');

  const { filters, setFilters, clear } = useFilterState<DocumentPagesFilters>({
    storageKey: STORAGE_KEY,
    defaults: defaultFilters,
    onFilterChange,
    overrides: urlOverrides(searchParams),
  });

  React.useEffect(() => {
    sessionStorage.setItem(VIEW_MODE_KEY, viewMode);
    onViewModeChange?.(viewMode);
  }, [viewMode, onViewModeChange]);

  const handleCategoryChange = (value: string) => {
    const category = value === '' ? null : value;
    const newFilters = { ...filters, category };
    setFilters(newFilters);
  };

  const handleClassificationChange = (value: string) => {
    const classification = value === '' ? null : value;
    const newFilters = { ...filters, classification };
    setFilters(newFilters);
  };

  const handleStatusChange = (value: string) => {
    const newFilters = {
      ...filters,
      status: value as DocumentPagesFilters['status'],
    };
    setFilters(newFilters);
  };

  const handleTagChange = (value: string) => {
    const selectedTag = value;
    if (!selectedTag) {
      const newFilters = { ...filters, tags: [] };
      setFilters(newFilters);
      return;
    }

    if (!filters.tags.includes(selectedTag)) {
      const newFilters = {
        ...filters,
        tags: [...filters.tags, selectedTag],
      };
      setFilters(newFilters);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newFilters = {
      ...filters,
      tags: filters.tags.filter((tag) => tag !== tagToRemove),
    };
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    clear();
  };

  const handleClassificationScoreChange = (value: string) => {
    const newFilters = { ...filters, classificationScore: value };
    setFilters(newFilters);
  };

  const handleProjectIdChange = (value: string) => {
    const newFilters = { ...filters, projectId: value };
    setFilters(newFilters);
  };

  const hasActiveFilters =
    filters.category ||
    filters.projectId ||
    filters.status ||
    filters.classification ||
    filters.classificationScore ||
    uploadId ||
    onlyIds.length > 0 ||
    filters.tags.length > 0;

  return (
    <div className="rounded-lg mb-6">
      {/* Filter content - hidden on mobile (mobile-expand toggle removed); always visible from sm+. */}
      <div className="hidden sm:block">
        <div className="flex gap-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <ProjectIdInputFilter filters={filters} handleProjectIdChange={handleProjectIdChange} />

            <CategoryFilter filters={filters} handleCategoryChange={handleCategoryChange} />

            <StatusFilter filters={filters} handleStatusChange={handleStatusChange} />

            <ClassificationFilter
              filters={filters}
              handleClassificationChange={handleClassificationChange}
              categories={categories.data?.pageCategories?.map((category) => ({
                label: category,
                value: category,
              }))}
            />

            <ClassificationScoreFilter filters={filters} onChange={handleClassificationScoreChange} />

            <TagsFilter filters={filters} handleTagChange={handleTagChange} />
          </div>

          <div className="flex gap-2 items-end justify-end">
            <Button
              color={viewMode === 'list' ? 'dark/white' : 'white/dark'}
              className="md:px-4 md:py-2"
              onClick={() => setViewMode('list')}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
            >
              <FaList className="size-6 scale-70" />
            </Button>
            <Button
              color={viewMode === 'grid' ? 'dark/white' : 'white/dark'}
              className="md:px-4 md:py-2"
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <IoGridOutline className="size-6 scale-70" />
            </Button>
          </div>
        </div>

        {/* Active Filters Display */}
        <ActiveFiltersContainer handleClearFilters={handleClearFilters} hasActiveFilters={!!hasActiveFilters}>
          {filters.projectId && (
            <ActiveFilterItemIndicator
              label="Project ID"
              value={filters.projectId}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => handleProjectIdChange('')}
            />
          )}
          {filters.category && (
            <ActiveFilterItemIndicator
              label="Category"
              value={categoryFilterLabel(filters.category)}
              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              onRemove={() => handleCategoryChange('')}
            />
          )}
          {filters.classification && (
            <ActiveFilterItemIndicator
              label="Classification"
              value={filters.classification}
              className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              onRemove={() => handleClassificationChange('')}
            />
          )}

          {filters.classificationScore && (
            <ActiveFilterItemIndicator
              label="Classification Score"
              value={filters.classificationScore}
              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              onRemove={() => handleClassificationScoreChange('')}
            />
          )}
          {filters.status !== '' && (
            <ActiveFilterItemIndicator
              label="Status"
              value={filters.status}
              className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
              onRemove={() => handleStatusChange('')}
            />
          )}
          {filters.tags.map((tag) => (
            <ActiveFilterItemIndicator
              key={tag}
              label="Tag"
              value={tag}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => handleRemoveTag(tag)}
            />
          ))}
          {onlyIds.map((id) => (
            <ActiveFilterItemIndicator
              key={id}
              label="ID"
              value={`${id.slice(0, 4)}...${id.slice(-4)}`}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('ids', id);
                setSearchParams(newParams);
              }}
            />
          ))}
        </ActiveFiltersContainer>
      </div>
    </div>
  );
}
